import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  ensureChatThread,
  fetchRoomMessages,
  sendRoomMessage,
} from "../services/chatService";
import { CHAT_SOCKET_URL } from "../config/AppConfig";

const DEFAULT_LIMIT = 50;

const parseRoomId = (roomId) => {
  if (!roomId || typeof roomId !== "string") return {};
  const parts = roomId.split("_");
  if (parts.length < 4 || parts[0] !== "chat") return {};
  const [, productId, buyerId, sellerId] = parts;
  return { productId, buyerId, sellerId };
};

const toIsoString = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const normalizeMessage = (message = {}) => {
  const explicitTimestamp =
    message.sendMessageAt ||
    message.send_message_at ||
    message.createdAt ||
    message.created_at ||
    message.timestamp ||
    message.updated_at ||
    message.updatedAt;

  const timestampSource =
    explicitTimestamp || (message.pending ? new Date().toISOString() : null);

  const createdAt = toIsoString(timestampSource);

  const senderIdRaw =
    message.senderId ??
    message.sender_id ??
    message.userId ??
    message.user_id ??
    message.sender ??
    message.from;

  const roomId =
    message.roomId ||
    message.room_id ||
    (message.product_id &&
      message.buyer_id &&
      message.seller_id &&
      `chat_${message.product_id}_${message.buyer_id}_${message.seller_id}`);

  const messageId =
    message.message_id ||
    message.id ||
    message._id ||
    `${roomId || "message"}-${createdAt || Date.now()}`;

  return {
    id: messageId.toString(),
    roomId,
    senderId:
      senderIdRaw === undefined || senderIdRaw === null
        ? undefined
        : senderIdRaw.toString(),
    message: message.message ?? message.text ?? "",
    createdAt: createdAt || null,
    pending: Boolean(message.pending),
  };
};

const mergeMessages = (existing, incoming) => {
  const map = new Map();
  [...existing, ...incoming].forEach((msg) => {
    const key =
      (msg.id || `${msg.roomId || "room"}-${msg.createdAt || "ts"}`)?.toString();
    if (!key) return;
    map.set(key, msg);
  });
  return Array.from(map.values()).sort(
    (a, b) =>
      new Date(a.createdAt || 0).getTime() -
      new Date(b.createdAt || 0).getTime()
  );
};

const removeFirstPendingForSender = (collection, senderId) => {
  if (!senderId) return collection;
  let removed = false;
  return collection.filter((msg) => {
    if (
      !removed &&
      msg.pending &&
      msg.senderId?.toString() === senderId.toString()
    ) {
      removed = true;
      return false;
    }
    return true;
  });
};

const useChatRoom = ({
  productId: inputProduct,
  buyerId: inputBuyer,
  sellerId: inputSeller,
  roomId: initialRoomId,
  currentUserId,
  limit = DEFAULT_LIMIT,
}) => {
  const derivedIds = useMemo(() => {
    const parsed = initialRoomId ? parseRoomId(initialRoomId) : {};
    return {
      productId: inputProduct || parsed.productId,
      buyerId: inputBuyer || parsed.buyerId,
      sellerId: inputSeller || parsed.sellerId,
    };
  }, [initialRoomId, inputProduct, inputBuyer, inputSeller]);

  const { productId, buyerId, sellerId } = derivedIds;

  const [roomId, setRoomId] = useState(initialRoomId || "");
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [hasMore, setHasMore] = useState(true);

  const socketRef = useRef(null);
  const reconnectTimer = useRef(null);
  const cursorRef = useRef(null);
  const joiningRef = useRef(false);

  const resetState = useCallback(() => {
    cursorRef.current = null;
    setMessages([]);
    setHasMore(true);
  }, []);

  const loadHistory = useCallback(
    async (targetRoomId, { reset = false } = {}) => {
      if (!targetRoomId) return;
      try {
        const params = { limit };
        if (!reset && cursorRef.current) {
          params.before = cursorRef.current;
        }
        const data = await fetchRoomMessages(targetRoomId, params);
        const normalized = Array.isArray(data)
          ? data.map(normalizeMessage)
          : [];

        if (normalized.length) {
          const earliestWithTimestamp = normalized.find((msg) => msg.createdAt);
          if (earliestWithTimestamp?.createdAt) {
            cursorRef.current = earliestWithTimestamp.createdAt;
          }
        }

        if (normalized.length < limit) {
          setHasMore(false);
        }

        setMessages((prev) =>
          reset ? normalized : mergeMessages(normalized, prev)
        );
      } catch (err) {
        console.error("Chat history error:", err);
        setError(err);
      }
    },
    [limit]
  );

  const connectSocket = useCallback(
    (resolvedRoomId) => {
      if (!resolvedRoomId) return;

      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      const serverUrl = [
        // "http://localhost:3000",      // local dev
        // "http://localhost:5173",      // another dev frontend
        // "https://winkdic.com",        // production frontend
        "https://winkdic.com"     // production subdomain
      ]

      // const socket = io("https://winkdic.com", {
      //   path: "/socket.io",
      //   transports: ["websocket"],
      //   withCredentials: true,
      // });



      const socket = io("https://winkdic.com", {
        transports: ["websocket"],
        withCredentials: true,
        path: "/2025/B2B/server/socket.io",   // IMPORTANT
        reconnectionAttempts: 5,
        reconnectionDelay: 1500,
        auth: {
          roomId: resolvedRoomId,
        },
      });

      socketRef.current = socket;

      socket.on("connect", () => {
        setStatus("connected");
        socket.emit(
          "join_room",
          { productId, buyerId, sellerId },
          (ack) => {
            if (ack?.ok) {
              if (ack.roomId) setRoomId(ack.roomId);

              if (Array.isArray(ack.history)) {
                const normalized = ack.history.map(normalizeMessage);
                setMessages((prev) => mergeMessages(prev, normalized));
              }
            }
          }
        );
      });

      socket.on("disconnect", () => {
        setStatus("disconnected");
        reconnectTimer.current = setTimeout(() => {
          connectSocket(resolvedRoomId);
        }, 2000);
      });

      socket.io.on("error", (err) => {
        console.error("Socket error:", err);
        setError(err);

        if (socketRef.current) {
          try {
            socketRef.current.disconnect();
          } catch (e) {
            console.error("Socket disconnect error:", e);
          }
        }

        socketRef.current = null;
      });

      socket.on("chat_history", (history) => {
        if (Array.isArray(history)) {
          setMessages((prev) =>
            mergeMessages(prev, history.map(normalizeMessage))
          );
        }
      });

      socket.on("new_message", (incoming) => {
        const normalized = normalizeMessage(incoming);
        setMessages((prev) => {
          const base =
            normalized.senderId &&
            normalized.senderId.toString() === currentUserId?.toString()
              ? removeFirstPendingForSender(prev, normalized.senderId)
              : prev;
          return mergeMessages(base, [normalized]);
        });
      });

      socket.on("typing", ({ senderId, isTyping }) => {
        if (!senderId || senderId === currentUserId) return;
        setTypingUsers((prev) => ({
          ...prev,
          [senderId]: isTyping,
        }));
      });
    },
    [buyerId, productId, sellerId, currentUserId]
  );


  // const connectSocket = useCallback(
  //   (resolvedRoomId) => {
  //     if (!resolvedRoomId) return;

  //     if (socketRef.current) {
  //       socketRef.current.disconnect();
  //     }

  //     const socket = io(CHAT_SOCKET_URL, {
  //       transports: ["websocket"],
  //       reconnectionAttempts: 5,
  //       auth: {
  //         roomId: resolvedRoomId,
  //       },
  //     });

  //     socketRef.current = socket;

  //     socket.on("connect", () => {
  //       setStatus("connected");
  //       socket.emit("join_room", { productId, buyerId, sellerId }, (ack) => {
  //         if (ack?.ok) {
  //           if (ack.roomId) {
  //             setRoomId(ack.roomId);
  //           }
  //           if (Array.isArray(ack.history)) {
  //             const normalizedHistory = ack.history.map(normalizeMessage);
  //             setMessages((prev) =>
  //               mergeMessages(prev, normalizedHistory)
  //             );
  //           }
  //         }
  //       });
  //     });

  //     socket.on("disconnect", () => {
  //       setStatus("disconnected");
  //       reconnectTimer.current = setTimeout(() => {
  //         connectSocket(resolvedRoomId);
  //       }, 2000);
  //     });

  //     socket.io.on("error", (err) => {
  //       console.error("Socket error:", err);
  //       // अगर socket connect नहीं हो पा रहा (जैसे timeout), तो
  //       // socketRef को null कर देते हैं ताकि sendMessage HTTP fallback यूज़ करे।
  //       setError(err);
  //       if (socketRef.current) {
  //         try {
  //           socketRef.current.disconnect();
  //         } catch (e) {
  //           console.error("Socket disconnect error:", e);
  //         }
  //       }
  //       socketRef.current = null;
  //     });

  //     socket.on("chat_history", (history) => {
  //       if (Array.isArray(history)) {
  //         setMessages((prev) =>
  //           mergeMessages(prev, history.map(normalizeMessage))
  //         );
  //       }
  //     });

  //     socket.on("new_message", (incoming) => {
  //       const normalized = normalizeMessage(incoming);
  //       setMessages((prev) => {
  //         const filtered = prev.filter(
  //           (msg) =>
  //             !(
  //               msg.pending &&
  //               msg.senderId === normalized.senderId &&
  //               msg.message === normalized.message
  //             )
  //         );
  //         return mergeMessages(filtered, [normalized]);
  //       });
  //     });

  //     socket.on("typing", ({ senderId, isTyping }) => {
  //       if (!senderId || senderId === currentUserId) return;
  //       setTypingUsers((prev) => ({
  //         ...prev,
  //         [senderId]: isTyping,
  //       }));
  //     });
  //   },
  //   [buyerId, productId, sellerId, currentUserId]
  // );

  const initializeRoom = useCallback(async () => {
    if (!productId || !buyerId || !sellerId) return;
    if (joiningRef.current) return;

    joiningRef.current = true;
    setStatus("initializing");
    setError(null);
    resetState();

    try {
      const thread = await ensureChatThread({ productId, buyerId, sellerId });
      let ensuredRoomId =
        thread.roomId ||
        thread.room_id ||
        roomId ||
        `chat_${productId}_${buyerId}_${sellerId}`;
      setRoomId(ensuredRoomId);

      await loadHistory(ensuredRoomId, { reset: true });
      connectSocket(ensuredRoomId);
      setStatus("ready");
    } catch (err) {
      console.error("Chat init error:", err);
      setError(err);
      setStatus("error");
    } finally {
      joiningRef.current = false;
    }
  }, [buyerId, sellerId, productId, roomId, resetState, loadHistory, connectSocket]);

  useEffect(() => {
    initializeRoom();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [initializeRoom]);

  const sendMessage = useCallback(
    async (text) => {
      if (!text?.trim() || !roomId) return;
      const trimmed = text.trim();
      const optimistic = normalizeMessage({
        id: `temp-${Date.now()}`,
        roomId,
        sender_id: currentUserId,
        message: trimmed,
        created_at: new Date().toISOString(),
        pending: true,
      });


      setMessages((prev) => mergeMessages(prev, [optimistic]));

      const socket = socketRef.current;
      const socketConnected = socket?.connected;

      if (socket && socketConnected) {
        socket.emit("send_message", {
          roomId,
          senderId: currentUserId,
          message: trimmed,
        });
      } else {
        try {
          const saved = await sendRoomMessage(roomId, {
            senderId: currentUserId,
            message: trimmed,
          });

          setMessages((prev) =>
            mergeMessages(
              prev.filter((msg) => msg.id !== optimistic.id),
              [normalizeMessage(saved)]
            )
          );
        } catch (err) {
          console.error("Fallback send error:", err);
          setError(err);
        }
      }
    },
    [roomId, currentUserId]
  );

  const startTyping = useCallback(() => {
    if (!socketRef.current || !roomId) return;
    socketRef.current.emit("typing", {
      roomId,
      senderId: currentUserId,
      isTyping: true,
    });
  }, [roomId, currentUserId]);

  const stopTyping = useCallback(() => {
    if (!socketRef.current || !roomId) return;
    socketRef.current.emit("typing", {
      roomId,
      senderId: currentUserId,
      isTyping: false,
    });
  }, [roomId, currentUserId]);

  const fetchMoreHistory = useCallback(() => {
    if (!roomId || !hasMore) return;
    return loadHistory(roomId);
  }, [roomId, hasMore, loadHistory]);

  const partnerTyping = useMemo(
    () => Object.entries(typingUsers).some(([, value]) => value),
    [typingUsers]
  );

  const updateMessageLocally = useCallback((messageId, updates) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id?.toString() === messageId?.toString()
          ? { ...msg, ...updates }
          : msg
      )
    );
  }, []);
  
  const removeMessageLocally = useCallback((messageId) => {
    setMessages((prev) =>
      prev.filter((msg) => msg.id?.toString() !== messageId?.toString())
    );
  }, []);

  const clearAllMessages = useCallback(() => {
    cursorRef.current = null;
    setMessages([]);
    setHasMore(false);
  }, []);

  return {
    roomId,
    messages,
    status,
    error,
    hasMore,
    typingUsers,
    partnerTyping,
    sendMessage,
    fetchMoreHistory,
    startTyping,
    stopTyping,
    updateMessageLocally,
    removeMessageLocally,
    clearAllMessages,
  };
};

export { parseRoomId };
export default useChatRoom;

